import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch } from 'src/hooks/store';
import { signIn } from 'src/slices/auth';
import * as Yup from 'yup';
import { theme } from '../../constants/theme';

const useStyles = createUseStyles({
  signInForm: {},
  signInFormContainer: {},
  signInEmail: {},
  signInPassword: {},
  signInActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 0),
  },
  signInActionsRemember: {},
  signInActionsForgotPassword: {},
  signInSubmitButton: {},
});

// ----------------------------------------------------------------------

const SignInForm: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [error, setError] = useState<string | undefined>(undefined);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();

  const SignInSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('forms.rules.email') || '')
      .required(t('forms.rules.required') || ''),
    password: Yup.string().required(t('forms.rules.required') || ''),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: SignInSchema,
    onSubmit: async (values) => {
      if (error) {
        setError(undefined);
      }
      const resultAction = await dispatch(
        signIn({ email: values.email, password: values.password }),
      );
      if (signIn.rejected.match(resultAction)) {
        setError(resultAction.error.message);
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form className={classes.signInForm} noValidate onSubmit={handleSubmit}>
        <Stack className={classes.signInFormContainer} spacing={3}>
          <TextField
            className={classes.signInEmail}
            fullWidth
            type="email"
            label={t('common.email')}
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            className={classes.signInPassword}
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label={t('common.password')}
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack className={classes.signInActionsContainer}>
          <Link variant="subtitle2" component={RouterLink} to="/signUp">
            {t('pages.SignIn.createAccount')}
          </Link>
          <Link
            className={classes.signInActionsForgotPassword}
            component={RouterLink}
            variant="subtitle2"
            to="/lostPassword"
          >
            {t('pages.SignIn.lostPassword')}
          </Link>
        </Stack>

        <LoadingButton
          className={classes.signInSubmitButton}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          {t('common.logIn')}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
};

export default SignInForm;
